// @flow
import React, { PureComponent, type Node } from "react";
import InfiniteLoader from "react-virtualized/dist/commonjs/InfiniteLoader";
import List from "react-virtualized/dist/commonjs/List";
import CellMeasurer from "react-virtualized/dist/commonjs/CellMeasurer";
import CellMeasurerCache from "react-virtualized/dist/commonjs/CellMeasurer/CellMeasurerCache";
import Measure from "react-measure";
import { produce } from "immer";
import Icons from "@hs/icons";
import RowResult from "./RowResult";
import Constants, { type ConstantsEnum } from "data/constants";
import { type SearchResultDTO, type SearchResultRowDTO } from "data/types";
import NoData from "components/NoData";
import ContextMenuHandler from "components/ContextMenu/Handler";
import styles from "./SearchResult.module.css";

// #39038 ES limits paged results to 10000 rows
const FT_ROWSLIMIT = 10000;

type Props = {
    /** words to highlight in results */
    terms: Array<string>,
    /** search results */
    result: SearchResultDTO,
    /** callback to load next page
     * @param {number} pageNum which page number to load
     */
    loadMoreRows: (pageNum: number) => Promise<SearchResultDTO>,
    /** callback when user clicks on a row
     * @param {number} index the row index clicked
     * @param {SearchResultRowDTO} row the row object itself
     * @param {boolean} newWindow whether requesting new window (ctrlKey or altKey)
     */
    onClick: (
        index: number,
        row: SearchResultRowDTO,
        newWindow: boolean
    ) => void,
    /** callback when user clicks on a row More... button
     * @param {number} index the row index clicked
     * @param {SearchResultRowDTO} row the row object itself
     */
    onMoreClick: (index: number, row: SearchResultRowDTO) => void,
    /** search result row currently active (-1 for none) */
    activeRowIndex: number,
    /** whether breadcrumbs should allow navigation */
    isBreadcrumbReadonly?: boolean,
    /** to which row to scroll to */
    scrollToRow?: ?number,
    /** Callback after rows have been loaded */
    handleMoreRowsLoaded: (
        rows: Array<any>,
        totalCount: number,
        updateTotalCount: boolean
    ) => void,
};

type State = {
    rows: any,
    pageSize: number,
    maxTotal: number,
    pagesStatus: Array<ConstantsEnum>,
    scrollToRow: number,
    hoveredRowIndex: number,
};

/**
 * renders flat list of search results
 */
export default class SearchResult extends PureComponent<Props, State> {
    _cache: Object;
    _list: List;
    _isMounted: boolean;

    constructor(props: Props, children?: Node) {
        super(props);
        this._isMounted = false;
        this._cache = new CellMeasurerCache({
            fixedWidth: true,
            minHeight: 40,
        });

        this.state = {
            //first page results loaded by SearchResultContainer
            rows: props.result.rows,
            pageSize: props.result.pageSize,
            maxTotal: FT_ROWSLIMIT - props.result.pageSize,
            //first page already pre-loaded otherwise we wouldn't be here
            pagesStatus: [Constants.OK],
            scrollToRow: props.scrollToRow || -1,
            hoveredRowIndex: -1,
        };
    }
    componentDidMount = () => (this._isMounted = true);
    componentWillUnmount = () => (this._isMounted = false);

    componentDidUpdate(prevProps: Props) {
        /* istanbul ignore else */
        if (prevProps.result !== this.props.result) {
            /* istanbul ignore next */
            this._cache.clearAll();
            this.setState(
                {
                    rows: this.props.result.rows,
                    pagesStatus: [Constants.OK],
                    scrollToRow: -1,
                },
                () => this._list.scrollToPosition(0)
            );
        }
        /* istanbul ignore else */
        if (prevProps.scrollToRow !== this.props.scrollToRow) {
            this.setState({
                scrollToRow: this.props.scrollToRow,
            });
        }
    }

    /* istanbul ignore next */ _onResize = (width: number, height: number) =>
        /* istanbul ignore next */
        this._cache.clearAll();

    _isRowLoaded = ({ index }: { index: number }) =>
        Object.prototype.hasOwnProperty.call(this.state.rows, index);

    _loadMoreRows = ({
        startIndex,
        stopIndex,
    }: {
        startIndex: number,
        stopIndex: number,
    }) => {
        const { rows, pageSize, pagesStatus } = this.state;
        //console.log("loadMoreRows", startIndex, stopIndex);

        // result already has data?
        /* istanbul ignore next */
        if (
            Object.prototype.hasOwnProperty.call(rows, startIndex) &&
            Object.prototype.hasOwnProperty.call(rows, stopIndex)
        ) {
            return;
        }

        // calc pagenumber
        let pageNum = 0;
        /* istanbul ignore else */
        if (startIndex > 0) {
            // specific row index requested? calc page number
            pageNum = Math.floor(startIndex / pageSize);
        }
        // check that stopIndex is indeed included
        /* istanbul ignore next */
        if ((pageNum + 1) * pageSize < stopIndex) {
            pageNum++;
        }
        // page already loaded/requested?
        /* istanbul ignore next */
        if (
            pagesStatus[pageNum] === Constants.OK ||
            pagesStatus[pageNum] === Constants.LOADING
        )
            return;

        // fetch
        this.setState({
            pageStatus: produce(this.state.pagesStatus, (draft) => {
                draft[pageNum] = Constants.LOADING;
            }),
        });

        return this.props.loadMoreRows(pageNum).then(
            (result) =>
                this._isMounted &&
                this.setState((state) => {
                    const nextRows = produce(state.rows, (draft) => {
                        Object.assign(draft, result.rows);
                    });
                    this.props.handleMoreRowsLoaded(
                        nextRows,
                        result.totalCount
                    );
                    return {
                        rows: nextRows,
                        pagesStatus: produce(state.pagesStatus, (draft) => {
                            draft[pageNum] = Constants.OK;
                        }),
                    };
                })
        );
    };

    _rowRenderer = ({
        index,
        isScrolling,
        key,
        parent,
        style,
    }: {
        index: number,
        isScrolling: boolean,
        key: number,
        parent: Object,
        style: StyleSheet,
    }) => {
        const { rows } = this.state;
        const onMouseEnter = (e) => this.setState({ hoveredRowIndex: index });
        const onMoreClick =
            typeof this.props.onMoreClick === "function"
                ? (e) => this.props.onMoreClick(e, index, rows[index])
                : null;

        // result already has data?
        if (this._isRowLoaded({ index })) {
            return (
                <CellMeasurer
                    cache={this._cache}
                    columnIndex={0}
                    key={key}
                    rowIndex={index}
                    parent={parent}
                >
                    {({ measure }) => (
                        <ContextMenuHandler
                            rowIndex={index}
                            componentClass={RowResult}
                            onClick={this.props.onClick}
                            onMoreClick={onMoreClick}
                            onMouseEnter={onMouseEnter}
                            isActive={this.props.activeRowIndex === index}
                            isHovered={this.state.hoveredRowIndex === index}
                            terms={this.props.terms}
                            onLoad={measure}
                            index={index}
                            style={style}
                            row={rows[index]}
                            isBreadcrumbReadonly={
                                this.props.isBreadcrumbReadonly
                            }
                            onContextMenu={onMoreClick}
                        />
                    )}
                </CellMeasurer>
            );
        } else {
            return (
                <CellMeasurer
                    cache={this._cache}
                    columnIndex={0}
                    key={key}
                    rowIndex={index}
                    parent={parent}
                >
                    {({ measure }) => (
                        <div
                            className={styles.placeholder}
                            onLoad={measure}
                            style={style}
                        >
                            <Icons.Library
                                name="spinner"
                                className="fa-pulse"
                            />
                        </div>
                    )}
                </CellMeasurer>
            );
        }
    };

    render() {
        const { result } = this.props;
        const { rows, maxTotal } = this.state;
        return (
            <Measure bounds onResize={this._onResize}>
                {({
                    measureRef,
                    contentRect: /* istanbul ignore next */ { bounds } = {
                        bounds: { width: 0, height: 0 },
                    },
                }) => (
                    // $FlowFixMe
                    <div
                        ref={measureRef}
                        style={{
                            flex: "1 1 100%",
                            minHeight: "0",
                            minWidth: "0",
                        }}
                    >
                        <InfiniteLoader
                            isRowLoaded={this._isRowLoaded}
                            loadMoreRows={this._loadMoreRows}
                            rowCount={result.totalCount}
                        >
                            {({ onRowsRendered, registerChild }) => (
                                <List
                                    ref={(ref) => {
                                        this._list = ref;
                                        registerChild(ref);
                                    }}
                                    deferredMeasurementCache={this._cache}
                                    className={styles.list}
                                    width={
                                        /* istanbul ignore next */ bounds?.width ??
                                        0
                                    }
                                    height={
                                        /* istanbul ignore next */ bounds?.height ??
                                        0
                                    }
                                    onRowsRendered={onRowsRendered}
                                    overscanRowCount={0}
                                    rowCount={Math.min(
                                        result.totalCount,
                                        maxTotal
                                    )}
                                    rowHeight={this._cache.rowHeight}
                                    rowRenderer={this._rowRenderer}
                                    noRowsRenderer={() => (
                                        <NoData locContext="search" />
                                    )}
                                    rows={rows}
                                    scrollToIndex={this.state.scrollToRow}
                                    scrollToAlignment="center"
                                />
                            )}
                        </InfiniteLoader>
                    </div>
                )}
            </Measure>
        );
    }
}
